import { CaretRight, Trash } from "@phosphor-icons/react";
import {
  Banner,
  Button,
  Tab,
  TabList,
  TabPanel,
  TabProvider,
} from "@replicate/ui";
import { useQuery } from "@tanstack/react-query";
import { P, match } from "ts-pattern";
import { useQueryParam } from "../../hooks";
import { getInputSchema } from "../../schema";
import type {
  ErrorWithJSON,
  PipelinePrediction,
  Prediction,
} from "../../types";
import { route } from "../../urls";
import CodeBlock from "../code-block";
import ConditionalWrap from "../conditional-wrap";
import { RenderInPortal } from "../render-in-portal";
import { SharePredictionPopover } from "../share-prediction-popover";
import { useSvixCustomerContext } from "../svix/context";
import { TERMINAL_PREDICTION_STATUSES } from "./constants";
import { usePlaygroundContext } from "./context";
import { EntityMeta, PredictionMetaItems } from "./entity-meta";
import { useDockerImage, usePrediction } from "./hooks";
import { InputOutputGrid } from "./input-output-grid";
import { InputOverview } from "./input-overview";
import { DelayedPredictionDetailOutput } from "./prediction-detail-output";
import { PredictionDetailWebhookInfo } from "./prediction-detail-webhook-info";
import { RunWith, RunWithContext } from "./run-with";

export function PredictionDetail({
  additionalActions,
  area,
  isViewingWithSuperpowers = false,
  prediction,
  showHeader = true,
  showMeta = true,
  visualOptions,
}: {
  additionalActions?: React.ReactNode;
  area: "dashboard" | "explore";
  isViewingWithSuperpowers?: boolean;
  prediction: Prediction;
  showHeader?: boolean;
  showMeta?: boolean;
  visualOptions?: { hideDivider?: boolean };
}) {
  const { model, permissions, token, usesVersionlessApi, version } =
    usePlaygroundContext();
  const dockerImageQuery = usesVersionlessApi ? null : useDockerImage(version);

  const DEFAULT_TAB = "form";
  const [activeTabId, setActiveTabId] = useQueryParam("input", DEFAULT_TAB);

  const query = usePrediction({
    uuid: prediction.id,
  });

  const pipelineQuery = useQuery({
    queryKey: ["api_prediction_pipeline", prediction.id],
    async queryFn() {
      const res = await fetch(
        route("api_prediction_pipeline", {
          prediction_uuid: prediction.id,
        })
      );

      if (res.ok) {
        return res.json<PipelinePrediction>();
      }

      let error = {
        detail: "Failed to fetch pipeline prediction",
        status: res.status,
      };
      try {
        error = {
          ...error,
          ...(await res.json<ErrorWithJSON>()),
        };
      } catch (e) {}

      return Promise.reject(error);
    },
  });

  const predictionIsRunning =
    query.data && !TERMINAL_PREDICTION_STATUSES.includes(query.data.status);

  const svixCustomerContext = useSvixCustomerContext();
  const showWebhooksButton =
    // It's an API prediction.
    query.data?._extras.source === "api" &&
    // We have the customer context.
    svixCustomerContext &&
    // We've successfully fetched the prediction.
    query.data;

  return (
    <>
      {area === "dashboard" && query.data && (
        <RenderInPortal domNodeId="js-portal-share">
          <SharePredictionPopover prediction={query.data} />
        </RenderInPortal>
      )}
      {showHeader ? (
        <header className="mb-4">
          <h1 className="text-r8-3xl">Prediction</h1>
          {
            // TODO: What's tweak permission got to do with being able to see
            // what deployable ran the prediction?
            permissions.tweak &&
            query.data &&
            query.data._extras.ran_on.kind !== "hidden" ? (
              <a
                href={query.data._extras.ran_on.url}
                className="text-r8-lg text-r8-gray-11"
              >
                {match(query.data._extras.ran_on)
                  .with(
                    {
                      kind: "deployment",
                    },
                    (ran_on) =>
                      `${ran_on.deployment.username}/${ran_on.deployment.name}`
                  )
                  .with(
                    {
                      kind: "model",
                    },
                    (ran_on) => `${ran_on.model.username}/${ran_on.model.name}`
                  )
                  .with(
                    {
                      kind: "version",
                    },
                    (ran_on) =>
                      `${ran_on.model.username}/${
                        ran_on.model.name
                      }:${ran_on.version.id.slice(0, 8)}`
                  )
                  .exhaustive()}
              </a>
            ) : null
          }
        </header>
      ) : null}
      {area === "dashboard" && query.data?._extras.is_shared && (
        <Banner
          severity="info"
          flushTop
          condensed
          fullWidth
          centered
          description={
            <div>
              This prediction is shared. Anyone with the link can view it.{" "}
            </div>
          }
          meta={
            <Button
              size="xs"
              render={
                // biome-ignore lint/a11y/useAnchorContent: Content is injected by the component.
                <a
                  href={route("prediction_detail", {
                    prediction_uuid: query.data.id,
                  })}
                />
              }
              intent="info"
            >
              View public prediction
            </Button>
          }
        />
      )}
      <div className="prediction-detail">
        {showMeta ? (
          <div
            className={`py-4 ${
              visualOptions?.hideDivider ? "" : "prediction-meta"
            }`}
          >
            <EntityMeta>
              {match(query.data?._extras.ran_on)
                .with(
                  {
                    kind: "deployment",
                  },
                  () => (
                    <PredictionMetaItems.Deployment
                      prediction={query.data}
                      loading={query.isPending}
                    />
                  )
                )
                .with(
                  {
                    // OFFICIAL_MODEL_AUDIT(aron): Used to display official model badge
                    is_official: true,
                    kind: "model",
                  },
                  () => (
                    <PredictionMetaItems.OfficialModel
                      prediction={query.data}
                      loading={query.isPending}
                    />
                  )
                )
                .with(
                  {
                    kind: P.union("model", "version"),
                  },
                  () => (
                    <PredictionMetaItems.Model
                      prediction={query.data}
                      loading={query.isPending}
                    />
                  )
                )
                .with(
                  undefined,
                  {
                    kind: "hidden",
                  },
                  () => null
                )
                .exhaustive()}
              <PredictionMetaItems.Id
                prediction={query.data}
                loading={query.isPending}
              />
              {pipelineQuery.isSuccess && (
                <>
                  <PredictionMetaItems.ParentPrediction
                    pipelinePrediction={pipelineQuery.data}
                    loading={query.isPending || pipelineQuery.isPending}
                  />
                  <PredictionMetaItems.RootPrediction
                    pipelinePrediction={pipelineQuery.data}
                    loading={query.isPending || pipelineQuery.isPending}
                  />
                </>
              )}
              <PredictionMetaItems.Status
                prediction={query.data}
                loading={query.isPending}
              />
              <PredictionMetaItems.Source
                prediction={query.data}
                loading={query.isPending}
              />
              {match(query.data?._extras.ran_on)
                .with(
                  undefined,
                  {
                    kind: "hidden",
                  },
                  {
                    has_special_pricing: true,
                  },
                  () => null
                )
                .otherwise(() => (
                  <PredictionMetaItems.Hardware
                    prediction={query.data}
                    loading={query.isPending}
                  />
                ))}
              <PredictionMetaItems.TotalDuration
                prediction={query.data}
                loading={query.isPending}
              />
              <PredictionMetaItems.Created
                prediction={query.data}
                loading={query.isPending}
              />
              {showWebhooksButton ? (
                <EntityMeta.Item>
                  <EntityMeta.ItemLabel>Webhooks</EntityMeta.ItemLabel>
                  <EntityMeta.ItemValue loading={query.isPending}>
                    <div className="min-h-10">
                      <PredictionDetailWebhookInfo
                        prediction={query.data}
                        token={svixCustomerContext.token}
                        appId={svixCustomerContext.app_id}
                      />
                    </div>
                  </EntityMeta.ItemValue>
                </EntityMeta.Item>
              ) : null}
            </EntityMeta>
            {additionalActions ? (
              <div className="mt-8">
                <p className="mb-2 text-r8-sm text-r8-gray-11">
                  Additional actions
                </p>
                <div>{additionalActions}</div>
              </div>
            ) : null}
          </div>
        ) : null}
        {query.data?.data_removed ? (
          <div className="my-8 max-w-2xl prediction-data-deleted">
            <Banner
              icon={<Trash size={16} />}
              severity="warning"
              heading="Data has been removed"
              description="All input parameters, output values, and logs are automatically removed after an hour, by default, for predictions created through the API."
              meta={
                <Button
                  size="sm"
                  render={
                    // biome-ignore lint/a11y/useAnchorContent: Content is injected by the component.
                    <a href="/docs/how-does-replicate-work#delete-predictions" />
                  }
                  intent="warning"
                >
                  Learn more
                </Button>
              }
            />
          </div>
        ) : (
          <ConditionalWrap
            condition={isViewingWithSuperpowers}
            wrap={(children) => {
              return (
                <details className="prediction-detail-superpowers group">
                  <summary className="my-4">
                    <Banner
                      condensed
                      icon={
                        <CaretRight className="relative top-0.5 transform group-open:rotate-90" />
                      }
                      description="You are viewing with staff superpowers, so inputs and
                      outputs are hidden by default."
                    />
                  </summary>
                  {children}
                </details>
              );
            }}
          >
            <InputOutputGrid
              mobileOutputFirst
              input={
                <TabProvider
                  defaultActiveId={DEFAULT_TAB}
                  activeId={activeTabId}
                  setActiveId={(id) => {
                    setActiveTabId(id ?? undefined);
                  }}
                  selectedId={activeTabId ?? DEFAULT_TAB}
                >
                  <TabList responsive size="sm">
                    <Tab id="form">Form</Tab>
                    <Tab id="json">JSON</Tab>
                    {model ? (
                      <>
                        <Tab id="nodejs">Node.js</Tab>
                        <Tab id="python">Python</Tab>
                        <Tab id="http">HTTP</Tab>
                      </>
                    ) : null}
                    {dockerImageQuery?.isSuccess && (
                      <>
                        <Tab id="cog">Cog</Tab>
                        <Tab id="docker">Docker</Tab>
                      </>
                    )}
                  </TabList>
                  <TabPanel className="py-4" tabId="form">
                    <InputOverview
                      properties={
                        getInputSchema(prediction._extras.ran_on).properties
                      }
                      alwaysRenderURLsAsDownload={
                        prediction._extras.may_have_sensitive_output
                      }
                      input={prediction.input || {}}
                      reportFallback={
                        prediction._extras.ran_on.capabilities?.run ?? false
                      }
                    />
                  </TabPanel>
                  <TabPanel className="py-4" tabId="json">
                    <CodeBlock
                      language="json"
                      textContent={JSON.stringify(prediction.input, null, 2)}
                    />
                  </TabPanel>
                  {model ? (
                    <TabPanel className="py-4" tabId="nodejs">
                      {usesVersionlessApi ? (
                        <RunWith
                          context={RunWithContext.NodeJS}
                          input={prediction.input || {}}
                          model={model}
                          schemaObject={prediction._extras.ran_on}
                          token={token}
                          usesVersionlessApi
                        />
                      ) : (
                        <RunWith
                          context={RunWithContext.NodeJS}
                          input={prediction.input || {}}
                          schemaObject={prediction._extras.ran_on}
                          token={token}
                          usesVersionlessApi={false}
                          model={model}
                          version={version}
                        />
                      )}
                    </TabPanel>
                  ) : null}
                  {model ? (
                    <TabPanel className="py-4" tabId="python">
                      {usesVersionlessApi ? (
                        <RunWith
                          context={RunWithContext.Python}
                          input={prediction.input || {}}
                          model={model}
                          schemaObject={prediction._extras.ran_on}
                          token={token}
                          usesVersionlessApi
                        />
                      ) : (
                        <RunWith
                          context={RunWithContext.Python}
                          input={prediction.input || {}}
                          schemaObject={prediction._extras.ran_on}
                          token={token}
                          usesVersionlessApi={false}
                          model={model}
                          version={version}
                        />
                      )}
                    </TabPanel>
                  ) : null}
                  {model ? (
                    <TabPanel className="py-4" tabId="http">
                      {usesVersionlessApi ? (
                        <RunWith
                          context={RunWithContext.HTTP}
                          input={prediction.input || {}}
                          model={model}
                          schemaObject={prediction._extras.ran_on}
                          token={token}
                          usesVersionlessApi
                        />
                      ) : (
                        <RunWith
                          context={RunWithContext.HTTP}
                          input={prediction.input || {}}
                          schemaObject={prediction._extras.ran_on}
                          token={token}
                          usesVersionlessApi={false}
                          model={model}
                          version={version}
                        />
                      )}
                    </TabPanel>
                  ) : null}
                  {dockerImageQuery?.isSuccess && model && (
                    <>
                      <TabPanel className="py-4" tabId="cog">
                        {usesVersionlessApi ? (
                          <RunWith
                            context={RunWithContext.Cog}
                            dockerImageName={
                              dockerImageQuery.data.docker_image_name
                            }
                            input={prediction.input || {}}
                            model={model}
                            schemaObject={prediction._extras.ran_on}
                            token={token}
                            usesVersionlessApi
                          />
                        ) : (
                          <RunWith
                            context={RunWithContext.Cog}
                            dockerImageName={
                              dockerImageQuery.data.docker_image_name
                            }
                            input={prediction.input || {}}
                            model={model}
                            schemaObject={prediction._extras.ran_on}
                            token={token}
                            usesVersionlessApi={false}
                            version={version}
                          />
                        )}
                      </TabPanel>
                      <TabPanel className="py-4" tabId="docker">
                        {usesVersionlessApi ? (
                          <RunWith
                            context={RunWithContext.Docker}
                            dockerImageName={
                              dockerImageQuery.data.docker_image_name
                            }
                            input={prediction.input || {}}
                            model={model}
                            schemaObject={prediction._extras.ran_on}
                            token={token}
                            usesVersionlessApi
                          />
                        ) : (
                          <RunWith
                            context={RunWithContext.Docker}
                            dockerImageName={
                              dockerImageQuery.data.docker_image_name
                            }
                            input={prediction.input || {}}
                            model={model}
                            schemaObject={prediction._extras.ran_on}
                            token={token}
                            usesVersionlessApi={false}
                            version={version}
                          />
                        )}
                      </TabPanel>
                    </>
                  )}
                </TabProvider>
              }
              outputIsLoading={predictionIsRunning}
              output={
                query.data ? (
                  <DelayedPredictionDetailOutput
                    prediction={query.data}
                    queryStartedAt={query.dataUpdatedAt}
                  />
                ) : null
              }
            />
          </ConditionalWrap>
        )}
      </div>
    </>
  );
}
